import React, { useState } from "react";
import { EditorState } from "draft-js";
import { TChatsMessageForm } from "../../../../domain/codecs/form/ChatsMessageForm";
import { TCaseTimelineRoadblockOption } from "../../../../domain/codecs/form/CaseTimelineForm";
import { TStaffUser2 } from "../../../../domain/codecs/User";
import { TRoadblockAwaitableEntity } from "../../../../domain/codecs/form/RoadblockForm";
import { CRMRoadblockRichTextArea, HTML__TextAreaWithCommandsEditorContent } from "../CRMRoadblock/CRMRoadblockRichTextArea/CRMRoadblockRichTextArea";
import { createCommandList } from "../CRMRoadblock/CRMRoadblockViewChat/CRMRoadblockViewChatCommands";
import { getResetEditorState } from "../CRMRoadblock/CRMRoadblockRichTextArea/DraftJsUtils/getResetEditorState";
import { useCallbackOnUniquePropChange } from "../../hooks/useCallbackOnUniquePropChange";
import { CRMCaseTimelineRoadblockPicker } from "./CRMCaseTimelineRoadblockPicker";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Relative } from "../BuildingBlocks/Relative";
import { Absolute } from "../BuildingBlocks/Absolute";
import { Background } from "../BuildingBlocks/Background";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMLoadingBar } from "../Simple/CRMLoadingBar/CRMLoadingBar";
import { CRMRoadblockRichTextAreaInfoPill } from "../CRMRoadblock/CRMRoadblockRichTextAreaInfoPill/CRMRoadblockRichTextAreaInfoPill";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";

type TCaseTimelineComposerProps = {
    form: TChatsMessageForm;
    roadblockOptions: Array<TCaseTimelineRoadblockOption>;
    participants: Array<TStaffUser2>;
    awaitableEntities: Array<TRoadblockAwaitableEntity>;
    onTypeMessage: (form: TChatsMessageForm) => void;
    onSubmitMessage: (form: TChatsMessageForm) => void;
    onSelectRoadblock: (target: { roadblock_id: string, chats_id: string }) => void;
};

export const CRMCaseTimelineComposer = (props: React.PropsWithChildren<TCaseTimelineComposerProps>): JSX.Element => {

    const [editorState, setEditorState] = useState(() => EditorState.createWithContent(
        HTML__TextAreaWithCommandsEditorContent(props.form.edited.note_body)
    ));

    useCallbackOnUniquePropChange(
        (newStatus) => {
            if (newStatus === "success") {
                setEditorState(getResetEditorState(editorState));
            }
        },
        props.form.status
    );

    const IsSubmitting = props.form.status === "submitting";
    const HasNoRoadblocks = props.roadblockOptions.length === 0;
    const CommandList = createCommandList(props.participants, props.awaitableEntities);

    const getUpdatedForm = (html: string): TChatsMessageForm => ({
        ...props.form,
        edited: {
            ...props.form.edited,
            note_body: html
        }
    });

    const onTypeMessage = (newEditorState: EditorState, html: string) => {
        setEditorState(newEditorState);
        props.onTypeMessage(getUpdatedForm(html));
    };

    const onSubmitMessage = (_newEditorState: EditorState, html: string) => {
        props.onSubmitMessage(getUpdatedForm(html));
    };

    if (HasNoRoadblocks) {
        return (
            <CRMParagraph>
                Notes live on a roadblock, and this case doesn't have one yet. As soon as one opens you can add notes here.
            </CRMParagraph>
        );
    }

    return (
        <SpacingColumn spacing={CRMSpacing.SMALL}>
            {IsSubmitting
                ? <CRMLoadingBar duration={2500} />
                : <Background height="2px" />
            }

            {/* PICKER — pre-filled with the default roadblock; retarget as needed */}
            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                <CRMCaseTimelineRoadblockPicker
                    options={props.roadblockOptions}
                    selectedRoadblockId={props.form.edited.roadblock_id}
                    onSelect={props.onSelectRoadblock}
                />
            </SpacingRow>

            {/* EDITOR */}
            <Relative>
                <CRMRoadblockRichTextArea
                    placeholder="Write a note…"
                    editorState={editorState}
                    commandList={CommandList}
                    onChange={onTypeMessage}
                    onSubmit={onSubmitMessage}
                />
                <Absolute right="0px" bottom="3px">
                    <CRMRoadblockRichTextAreaInfoPill
                        chatForm={props.form}
                    />
                </Absolute>
            </Relative>
        </SpacingColumn>
    );
};
